import { debounce as _debounce, throttle as _throttle, cloneDeep } from 'lodash';
import { isEmptyObject } from './object';
import dayjs from 'dayjs';

/**
 * 使用此函数之前请先确保 理解并清楚防抖和节流的区别 以及 各自的使用场景
 * https://www.lodashjs.com/docs/lodash.debounce
 * @param {*} func 要防抖的函数。
 * @param {*} wait 需要防抖的毫秒。
 * @param {*} options 选项对象  ps:除非特殊场景 否则尽可能的 不要使用此参数。  add by wangdapan
 * @returns
 */
export function debounce<T extends(...args: any) => any>(func: T, wait = 10, options = {}) {
    return _debounce(func, wait, options);
}

/**
 * 立即执行版本的防抖函数
 * 第一次调用时立即执行，然后在 wait 毫秒内忽略后续调用
 * 适用场景：按钮点击防重复、表单提交等需要立即响应的场景
 * 
 * @param {*} func 要防抖的函数
 * @param {*} wait 防抖等待时间（毫秒），默认 300ms
 * @returns 防抖后的函数
 * 
 * @example
 * const handleClick = debounceImmediate(() => {
 *   console.log('立即执行，300ms 内后续点击被忽略');
 * }, 300);
 */
export function debounceImmediate<T extends(...args: any) => any>(func: T, wait = 300) {
    return _debounce(func, wait, {
        leading: true,  // 在延迟开始前调用
        trailing: false // 在延迟结束后不调用
    });
}

/**
 * 使用此函数之前请先确保 理解并清楚防抖和节流的区别 以及 各自的使用场景
 * https://www.lodashjs.com/docs/lodash.throttle
 * @param {*} func 要节流的函数。
 * @param {*} wait 需要节流的毫秒。
 * @param {*} options 选项对象  ps:除非特殊场景 否则尽可能的 不要使用此参数。  add by wangdapan
 * @returns
 */
export function throttle<T extends(...args: any) => any>(func: T, wait = 10, options = {}) {
    return _throttle(func, wait, options);
}
export { cloneDeep };


export { isEmptyObject };



// https://github.com/iamkun/dayjs
export function dateFormat(time: number, dataFormat: string = 'YYYY-MM-DD') {
    if (!time) {
        return '';
    }
    else {
        if (typeof time === 'number' && time.toString().length === 10) {
            time = time * 1000;
        }
    }
    let date = new Date(time);
    return dayjs(date).format(dataFormat || 'YYYY-MM-DD');
}


/**
 * 判断当前是否为测试/QA环境
 * PROXY_ENV=production 为线上环境，其他为线下环境
 */
export const isQAEnv = () => {
    return process.env.PROXY_ENV !== 'production';
}


/**
 * 使用location的内容
 */
export const getLocation = () => {
    const location = window.location;

    return {
        hashPath: location.hash,
        fullPath: location.href, // 包含参数的域名
        path: location.pathname,
    };
};


export const formatCurrentTime = () => {
    return dayjs(new Date().getTime()).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * 生成 uuid（优先使用 crypto.randomUUID，降级手写 v4）
 */
export function genUuid(): string {
    const c = (typeof crypto !== 'undefined' ? crypto : undefined) as Crypto | undefined;
    if (c && typeof c.randomUUID === 'function') {
        return c.randomUUID();
    }

    // RFC4122 v4 fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
        const r = (Math.random() * 16) | 0;
        const v = ch === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
